import { TabAnimator, Tabset } from '@squareup/dex-ui';
import React, {
  Children,
  FC,
  PropsWithChildren,
  ReactElement,
  isValidElement,
  useCallback,
  useState,
} from 'react';

interface MdxTabProps {
  title: string;
  children?: React.ReactNode;
}

const MdxTab: FC<PropsWithChildren<MdxTabProps>> = ({ children }) => {
  // Tab contents are rendered by the parent Tabs component
  return <>{children}</>;
};

MdxTab.displayName = 'MdxTab';

const MdxTabs: FC<PropsWithChildren> = ({ children }) => {
  const tabs: Array<{ id: string; label: string; contents: React.ReactNode }> =
    [];

  Children.forEach(children, (child, index) => {
    if (isValidElement(child)) {
      const props = child.props as Record<string, unknown>;
      const title = (props.title as string) || `Tab ${index + 1}`;
      // Include index so duplicate titles (or titles that slug to the same
      // string, e.g. special-char-only) still produce unique tab ids within
      // one <Tabs> group.
      const id = `${index}-${title.toLowerCase().replace(/\s+/g, '-')}`;
      tabs.push({
        id,
        label: title,
        contents: (child as ReactElement).props.children,
      });
    }
  });

  const [currentTabId, setCurrentTabId] = useState<string>(tabs[0]?.id || '');

  const onTabChanged = useCallback((newTabId: string) => {
    setCurrentTabId(newTabId);
  }, []);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <>
      <Tabset
        smallTabsetItem={true}
        tabs={tabs.map((tab) => ({ id: tab.id, label: tab.label }))}
        currentTabId={currentTabId}
        onTabChanged={onTabChanged}
      />
      <TabAnimator
        currentTab={currentTabId}
        tabs={tabs.map((tab) => ({
          tabId: tab.id,
          contents: tab.contents,
        }))}
      />
    </>
  );
};

MdxTabs.displayName = 'MdxTabs';

export { MdxTabs, MdxTab };
